import {getEnvironment, memoize, shallowMemoize} from '@sail/utils';
import {getTimeZoneData, supplemental} from '@stripe-internal/cldr';
import Globalize from 'globalize';
import {DEFAULT_LOCALE_DATA} from 'src/internal/constants';
import {IntlUnsupportedTimeZoneError} from 'src/internal/errors';
import handleGlobalizeError from 'src/internal/errors/handleGlobalizeError/handleGlobalizeError';
import getLocaleData from 'src/internal/getLocaleData';

import type {IReports} from '@sail/observability';
import type {CldrData, IntlConfig, Locale} from 'src/types';

const loadGlobalizeLocale = memoize((locale: Locale, reports: IReports) => {
  try {
    // @ts-expect-error - TS2349 - This expression is not callable.
    return Globalize(locale) as Globalize.Shared;
  } catch (error) {
    handleGlobalizeError(error, reports);
  }
});

const loadGlobalizeTimeZone = memoize((timeZone: string, reports: IReports) => {
  const zoneData = getTimeZoneData(timeZone);

  if (!zoneData) {
    const error = new IntlUnsupportedTimeZoneError(timeZone);

    reports.error(error, {project: 'sail_core'});

    if (getEnvironment() !== 'production') {
      throw error;
    }

    return;
  }

  Globalize.loadTimeZone(zoneData);
});

type CldrSource = CldrData | (typeof supplemental)[keyof typeof supplemental];

function feedGlobalize(cldr: CldrSource[], reports: IReports): void {
  cldr.forEach((data) => {
    try {
      data && Globalize.load(data);
    } catch (error) {
      handleGlobalizeError(error, reports);
    }
  });
}

function feedGlobalizeWithDefaults(reports: IReports): void {
  feedGlobalize(Object.values(supplemental), reports);
  feedGlobalize(Object.values(DEFAULT_LOCALE_DATA.cldr), reports);
}

const createGlobalizeWithLocale = shallowMemoize(
  (
    config: IntlConfig,
    requestedLocale: Locale,
    requestedTimeZone: string | undefined,
    reports: IReports,
  ): Globalize.Shared => {
    const localeData = getLocaleData(requestedLocale, config, reports);

    requestedTimeZone && loadGlobalizeTimeZone(requestedTimeZone, reports);

    feedGlobalizeWithDefaults(reports);
    localeData.cldr && feedGlobalize(Object.values(localeData.cldr), reports);

    const globalizeWithLocale = loadGlobalizeLocale(localeData.locale, reports);

    return globalizeWithLocale;
  },
);

export default createGlobalizeWithLocale;
